import { getCmsServiceClassFeatureList } from "../../../app/api/getCmsServiceClassFeatureList";
import { ICmsServiceClassFeatureList } from "../../../Orchestrator/Types/Interface/ICmsServiceClassFeatureList";
import { getServiceRequestCategoriesAPI } from "../../../app/api/getServiceRequestCategories";
import { getStops } from "../../../app/api/getStops";
import { BaseAction } from "../../../core/actions/BaseAction";
import { uuid } from "../../../core/utils/Uuid";
import { getRuntimeConfig } from "../../../Lib/Config/RuntimeConfig";
import stopManager, { getMappedStopCode } from "../../../Lib/Utils/StopManager";
import ticketOptionManager from "../../../Lib/Utils/TicketOptionManager";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse, IStop } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IAdditionalField, IServiceRequestCategory } from "../../../Orchestrator/Types/Interface/IServiceRequest";

class SetGroupBookingFieldsAction extends BaseAction<IDefaultActionResult, void> {

    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            data: false,
            message: "",
            status: "fail"
        }
        try {
            const selectedCategoryAndSubCategory: any = getRecoilStateValue("selectedCategoryAndSubCategoryAtom");
            
            if (!selectedCategoryAndSubCategory || 
                selectedCategoryAndSubCategory.category !== "GROUP_BOOKING_FORM" || 
                selectedCategoryAndSubCategory.subCategory !== "GROUP_BOOKING") {
                return actionResult;
            }

            let additionalFields: IAdditionalField[] = [];

            // Handle GROUP_BOOKING_FORM which is filtered out from serviceCategories
            const response: any = await getServiceRequestCategoriesAPI("NO_BOOKING");
            if (response.status === "ok" && response.answer) {
                let categories: IServiceRequestCategory[] = response?.answer?.serviceRequestCategories || response?.answer || [];
                const groupBookingCategory = categories.find(
                    (cat) => cat.name === "GROUP_BOOKING" && (cat.tags || []).length === 1 && cat.tags?.[0] === "GROUP_BOOKING_FORM"
                );
                
                if (groupBookingCategory) {
                    let modifiedAdditionalFields: IAdditionalField[] = groupBookingCategory?.additionalFields || [];
                    if ((groupBookingCategory?.additionalFields || []).length > 0) {
                        modifiedAdditionalFields = (groupBookingCategory.additionalFields || []).map((field) => ({
                            ...field,
                            fieldId: uuid()
                        }));
                    }

                    const isMultiPassenger =
                        modifiedAdditionalFields.filter(
                            (d: IAdditionalField) =>
                                d.type === "PASSENGER_NAME" &&
                                ["CURRENT_NAME", "NEW_NAME"].includes(d.title)
                        ).length >= 2;
                    if (!isMultiPassenger) {
                        const index = modifiedAdditionalFields.findIndex((item: IAdditionalField) => item.type === "PASSENGER_NAME" && ["FIRSTNAME_LASTNAME", "NAME"].includes(item.title));
                        if (index !== -1) {
                            modifiedAdditionalFields.splice(index, 1, {
                                ...modifiedAdditionalFields[index] || {},
                                fieldId: uuid(),
                                title: "PASSENGER_FIRST_NAME",
                                type: "TEXT",
                                width: "50%",
                            }, {
                                ...modifiedAdditionalFields[index] || {},
                                fieldId: uuid(),
                                title: "PASSENGER_LAST_NAME",
                                type: "TEXT",
                                width: "50%",
                            });
                        }
                    } else {
                        const index = modifiedAdditionalFields.findIndex(
                            (d: IAdditionalField) =>
                                d.type === "PASSENGER_NAME" &&
                                ["CURRENT_NAME", "NEW_NAME"].includes(d.title))
                        if (index !== -1) {
                            modifiedAdditionalFields.splice(index, 1, {
                                ...modifiedAdditionalFields[index] || {},
                                fieldId: uuid(),
                                type: "PASSENGER_NAME_CHANGE",
                            })
                            modifiedAdditionalFields = modifiedAdditionalFields.filter((d: any) => d.type !== "PASSENGER_NAME" && (d.title !== "CURRENT_NAME" || d.title !== "NEW_NAME"))
                        }
                    }

                    additionalFields = modifiedAdditionalFields;
                }
            }

            // CMS marks ENTER_ANY_SPECIFIC_DEMAND as required, but UX-wise the
            // "any specific demand?" prompt reads as optional. Force-optional
            // here so TitleField shows "(Optional)" and the required-check
            // skips it.
            // TODO: ask CMS team to mark this field optional at source, then
            // delete this block. Coupled to the field's title string — if CMS
            // renames it (pluralisation, etc.), the override silently stops.
            // A tag-based match (`field.tags?.includes("SPECIFIC_DEMAND")`)
            // would be stabler if CMS exposes a stable tag for it.
            additionalFields = additionalFields.map((field) =>
                field.title === "ENTER_ANY_SPECIFIC_DEMAND"
                    ? { ...field, required: false }
                    : field
            );

            // Handle station fields
            const isStationField = additionalFields?.find(d => d.type === "STATION")
            if (isStationField) {
                const config = await getRuntimeConfig();
                const stopExternalCode = config.NEXT_PUBLIC_STOP_EXTERNAL_CODE;
                const response: IApiResponse<{ stops: IStop[] }> = await getStops();
                if (response.status === "ok" && response.answer) {
                    const stops = response.answer.stops;
                    if (stops.length > 0) {
                        // Set dedupe keeps the FIRST occurrence per mapped code,
                        // while StopManager.stopsMap (StopManager.ts) builds a Map
                        // from the same stops which keeps the LAST. If backend ever
                        // returns two stops with the same mapped code, the dropdown
                        // would show the first stop's code but getStopNameById would
                        // resolve to the last stop's name. Known divergence; not
                        // currently tracked. Aligning StopManager to first-wins
                        // would touch every stop consumer — out of scope here.
                        const mappedStopValues = stopExternalCode
                            ? Array.from(new Set(
                                stops
                                    .map((stop) => getMappedStopCode(stop, stopExternalCode))
                                    .filter((value): value is string => Boolean(value))
                            ))
                            : [];
                        additionalFields = additionalFields.map(({ ...d }) => {
                            if (d.type === "STATION") {
                                d.values = mappedStopValues
                            }
                            return d
                        })
                    }
                }
            }

            // The dropdown is labelled "Class" (الدرجة), so it must list the
            // distinct travel classes — matching the mobile app — not every
            // fare/ticket-option. Listing every ticket-option surfaced
            // "Saver, Value, Flex, Saver, Value, Flex" because fare labels
            // repeat across the two classes.
            //
            // Both the class set and its (localized) labels come entirely from
            // the CMS service-class list — the same source the mobile
            // `ClassTicketOption` component uses — keeping only classes that
            // actually have bookable fares in the ticket-options response. No
            // class codes or labels are hardcoded here; if the CMS list is
            // unavailable the dropdown is left empty rather than surfacing raw
            // service-class codes.
            const isClassField = additionalFields?.find(d => d.type === "CLASS")
            if (isClassField) {
                try {
                    const ticketOptions = await ticketOptionManager.fetchTicketOptions();
                    if (ticketOptions && ticketOptions.length > 0) {
                        const presentClasses = new Set(
                            ticketOptions
                                .map(option => option.serviceClass)
                                .filter((serviceClass): serviceClass is string => Boolean(serviceClass))
                        );

                        let serviceClasses: ICmsServiceClassFeatureList = [];
                        try {
                            const cmsResponse = await getCmsServiceClassFeatureList();
                            if (cmsResponse.status === "ok" && cmsResponse.answer?.data) {
                                serviceClasses = cmsResponse.answer.data;
                            }
                        } catch (error) {
                            console.error("Failed to fetch CMS service classes for CLASS field", error);
                        }

                        // Keep CMS order, drop classes with no bookable fares.
                        const classes = serviceClasses.filter(sc => presentClasses.has(sc.service_class));
                        const values = classes.map(sc => sc.service_class);
                        const valueLabels = Object.fromEntries(
                            classes.map(sc => [sc.service_class, sc.name])
                        );
                        additionalFields = additionalFields.map(d =>
                            d.type === "CLASS" ? { ...d, values, valueLabels } : d
                        );
                    }
                } catch (error) {
                    console.error("Failed to fetch ticket options for CLASS field", error);
                }
            }
            const isOnlyHelpInfoField = additionalFields.find(d => d.type === "HELP_INFO")
            if (isOnlyHelpInfoField && additionalFields.length === 1) {
                updateRecoilState("submitRequestButtonDisabledAtom", false)
            } else {
                // Check if all fields are optional - not required
                const allFieldsOptional = additionalFields?.every(field => !field?.required)
                if (allFieldsOptional) {
                    updateRecoilState("submitRequestButtonDisabledAtom", false)
                } else {
                    updateRecoilState("submitRequestButtonDisabledAtom", true)
                }
            }
            updateRecoilState("additionalFieldsAtom", additionalFields)
            actionResult.status = "success"
        } catch (error) {
            actionResult.message = "Something went wrong"
        } finally {
            return actionResult
        }
    }
}

export const setGroupBookingFieldsAction = new SetGroupBookingFieldsAction()

